(() => {
  const initialized = new WeakSet();

  function parseEvents(json) {
    try {
      const raw = JSON.parse(json || "[]");
      return raw
        .map((event) => ({
          ...event,
          startDate: event.start ? new Date(event.start) : null,
          endDate: event.end ? new Date(event.end) : null,
        }))
        .filter((event) => event.startDate instanceof Date && !Number.isNaN(event.startDate.getTime()));
    } catch {
      return [];
    }
  }

  function isValidDate(value) {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }

  function isPast(event, now) {
    return isValidDate(event.endDate) ? event.endDate < now : event.startDate < now;
  }

  function sameDay(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  function isMultiDay(event) {
    return isValidDate(event.endDate) && !sameDay(event.startDate, event.endDate);
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatDateShort(date) {
    return date.toLocaleDateString([], {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function formatTimeShort(date) {
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  function formatDateRange(event) {
    if (!isValidDate(event.endDate) || sameDay(event.startDate, event.endDate)) {
      return formatDateShort(event.startDate);
    }
    return `${formatDateShort(event.startDate)} – ${formatDateShort(event.endDate)}`;
  }

  function formatTimeRange(event) {
    if (event.allDay) return "All day";
    if (!isValidDate(event.endDate)) return formatTimeShort(event.startDate);

    if (sameDay(event.startDate, event.endDate)) {
      return `${formatTimeShort(event.startDate)} – ${formatTimeShort(event.endDate)}`;
    }

    const endHasTime = event.endDate.getHours() !== 0 || event.endDate.getMinutes() !== 0;
    return endHasTime
      ? `${formatTimeShort(event.startDate)} → ${formatTimeShort(event.endDate)}`
      : formatTimeShort(event.startDate);
  }

  function computeLink(event, shopDomain, permanentDomain) {
    const candidate = (event.url || "").trim() || (event.detailUrl || "").trim();
    if (!candidate) {
      return { href: "", openNewTab: false };
    }

    const lower = candidate.toLowerCase();
    const shopLower = (shopDomain || "").toLowerCase();
    const permanentLower = (permanentDomain || "").toLowerCase();
    const isHttp = lower.startsWith("http://") || lower.startsWith("https://");

    if (!isHttp) {
      return { href: candidate, openNewTab: false };
    }

    const isInternal =
      (shopLower && lower.includes(shopLower)) ||
      (permanentLower && lower.includes(permanentLower));

    return {
      href: candidate,
      openNewTab: !isInternal,
    };
  }

  function buildTitle(link, title) {
    if (!link.href) {
      return `<span class="ec__titlenolink">${title}</span>`;
    }

    const targetAttrs = link.openNewTab ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<a class="ec__titlelink" href="${link.href}"${targetAttrs}>${title}</a>`;
  }

  function buildBanner(link, image, title) {
    if (!image) return "";

    const img = `<img class="ec__bannerimg" src="${image}" alt="">`;

    if (!link.href) {
      return `<div class="ec__banner">${img}</div>`;
    }

    const targetAttrs = link.openNewTab ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `
      <div class="ec__banner">
        <a class="ec__bannerlink" href="${link.href}"${targetAttrs} aria-label="${title}">
          ${img}
        </a>
      </div>
    `;
  }

  function renderEvent(event, now, shopDomain, permanentDomain) {
    const past = isPast(event, now);
    const multiDay = isMultiDay(event);
    const title = escapeHtml(event.title || "Event");
    const link = computeLink(event, shopDomain, permanentDomain);

    return `
      <article class="ec__row ${past ? "is-past" : ""}">
        ${buildBanner(link, event.image, title)}

        <div class="ec__rowbody">
          <div class="ec__meta">
            <div class="ec__date">${escapeHtml(formatDateRange(event))}</div>
            <div class="ec__time">${escapeHtml(formatTimeRange(event))}</div>
          </div>

          <div class="ec__main">
            <div class="ec__topline">
              ${event.category ? `<span class="ec__cat">${escapeHtml(event.category)}</span>` : ""}
              ${multiDay ? '<span class="ec__multibadge">Multi-day</span>' : ""}
              ${past ? '<span class="ec__pastbadge">Past</span>' : ""}
            </div>

            <div class="ec__title">
              ${buildTitle(link, title)}
            </div>

            ${event.location ? `<div class="ec__location">${escapeHtml(event.location)}</div>` : ""}
            ${event.descriptionHtml ? `<div class="ec__desc">${event.descriptionHtml}</div>` : ""}
          </div>
        </div>
      </article>
    `;
  }

  function initCalendar(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const section = root.closest("section");
    if (!section) return;

    const sectionId = root.dataset.sectionId;
    const dataEl = sectionId ? document.getElementById(`EC_DATA_${sectionId}`) : null;
    const eventsAll = parseEvents(dataEl?.textContent || "[]");

    const limit = Math.max(1, parseInt(root.dataset.limit || "50", 10) || 50);
    const defaultShowPast = root.dataset.defaultShowPast === "true";
    const shopDomain = root.dataset.shopDomain || "";
    const permanentDomain = root.dataset.permanentDomain || "";

    const filtersEl = section.querySelector("[data-ec-filters]");
    const searchEl = section.querySelector("[data-ec-search]");
    const showPastEl = section.querySelector("[data-ec-showpast]");
    const emptyEl = section.querySelector("[data-ec-empty]");

    let activeCategory = "ALL";
    let query = "";
    let showPast = defaultShowPast;

    if (showPastEl) {
      showPastEl.checked = showPast;
    }

    function getCategories() {
      const categories = new Set();
      for (const event of eventsAll) {
        const category = (event.category || "").trim();
        if (category) categories.add(category);
      }
      return [...categories].sort((a, b) => a.localeCompare(b));
    }

    function renderFilters() {
      if (!filtersEl) return;

      const categories = getCategories();
      filtersEl.innerHTML = [
        { label: "All", value: "ALL" },
        ...categories.map((category) => ({ label: category, value: category })),
      ]
        .map(({ label, value }) => {
          const pressed = value === activeCategory ? "true" : "false";
          return `<button type="button" class="ec__chip" data-cat="${escapeHtml(value)}" aria-pressed="${pressed}">${escapeHtml(label)}</button>`;
        })
        .join("");

      filtersEl.querySelectorAll("[data-cat]").forEach((button) => {
        button.addEventListener("click", () => {
          activeCategory = button.dataset.cat || "ALL";
          renderFilters();
          render();
        });
      });
    }

    function matches(event) {
      if (activeCategory !== "ALL" && (event.category || "").trim() !== activeCategory) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        event.title,
        event.location,
        event.category,
        event.descriptionHtml,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    }

    function render() {
      const now = new Date();

      let events = [...eventsAll]
        .sort((a, b) => a.startDate - b.startDate)
        .filter(matches);

      if (!showPast) {
        events = events.filter((event) => !isPast(event, now));
      }

      events = events.slice(0, limit);

      if (events.length === 0) {
        root.innerHTML = "";
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      if (emptyEl) emptyEl.hidden = true;
      root.innerHTML = events
        .map((event) => renderEvent(event, now, shopDomain, permanentDomain))
        .join("");
    }

    if (searchEl) {
      searchEl.addEventListener("input", (event) => {
        query = String(event.target.value || "").trim().toLowerCase();
        render();
      });
    }

    if (showPastEl) {
      showPastEl.addEventListener("change", () => {
        showPast = Boolean(showPastEl.checked);
        render();
      });
    }

    renderFilters();
    render();
  }

  function initAllCalendars() {
    document.querySelectorAll("[data-ec-root]").forEach(initCalendar);
  }

  document.addEventListener("DOMContentLoaded", initAllCalendars);
  document.addEventListener("shopify:section:load", initAllCalendars);
})();